import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useWallet } from '@txnlab/use-wallet-react';
import algosdk from 'algosdk';
import { httpsCallable } from 'firebase/functions';
import { 
    signInWithCustomToken, 
    signInWithPopup, 
    GoogleAuthProvider, 
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut as firebaseSignOut, 
    onAuthStateChanged 
} from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { db, functions, auth, firebaseUtils, type UserProfile } from '../lib/firebase';

// Standalone Mainnet algod client
const authAlgodClient = new algosdk.Algodv2('', 'https://mainnet-api.algonode.cloud', '');



// ── WebAuthn Utils ───────────────────────────────────────────────────────────

function base64urlToBuffer(base64url: string): ArrayBuffer {
    const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
    const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── Types ────────────────────────────────────────────────────────────────────

export type AuthMethod = 'wallet' | 'google' | 'passkey' | 'guest' | 'email' | 'discord' | null;

export interface LinkedMethods {
    walletAddress?: string;
    socialKey?: string;
    email?: string;
    providers: string[];
    walletLinked: boolean;
    hasPasskey?: boolean;
}

export interface GuestUser {
    isGuest: true;
    address: string;
    displayName: string;
}

export interface AuthContextType {
    activeAccount: any | null;
    signTransactions: (txns: Uint8Array[]) => Promise<(Uint8Array | null)[]>;
    socialUser: any | null;
    authMethod: AuthMethod;
    guestUser: GuestUser | null;
    isGuestOrConnected: boolean;
    isVerified: boolean;
    isVerifying: boolean;
    linkedMethods: LinkedMethods | null;
    isCryptoUser: boolean;
    brineCredits: number;
    verifyAccount: () => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signInWithPasskey: () => Promise<void>;
    signInWithEmail: (email: string, pass: string) => Promise<void>;
    signUpWithEmail: (email: string, pass: string) => Promise<void>;
    registerPasskey: () => Promise<void>;
    signOut: () => Promise<void>;
    refreshLinkedMethods: () => Promise<void>;
    loginAsGuest: () => void;
    logoutGuest: () => void;
    signInWithDiscord: () => void;
    unlinkWallet: () => Promise<void>;
    unlinkDiscord: () => Promise<void>;
    unlinkGoogle: () => Promise<void>;
    showAuthModal: boolean;
    setShowAuthModal: (open: boolean) => void;
    preferredAuthView: 'options' | 'wallet' | 'email';
    setPreferredAuthView: (view: 'options' | 'wallet' | 'email') => void;
    userProfile: UserProfile | null;
    refreshProfile: () => Promise<void>;
    refreshCredits: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const GUEST_NAMES = [
    "Popsicle Cheeks", "Captain Crunch", "Silly Goose", "Pickle Wizard",
    "Sir Pongs-a-lot", "Waffle Stomper", "Gummy Bear", "Noodle Arms"
];

export function AuthProvider({ children }: { children: ReactNode }) {
    const { activeAccount, signTransactions } = useWallet();
    const [socialUser, setSocialUser] = useState<any | null>(null);
    const [authMethod, setAuthMethod] = useState<AuthMethod>(null);
    const [guestUser, setGuestUser] = useState<GuestUser | null>(null);
    const [linkedMethods, setLinkedMethods] = useState<LinkedMethods | null>(null);
    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [preferredAuthView, setPreferredAuthView] = useState<'options' | 'wallet' | 'email'>('options');
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [brineCredits, setBrineCredits] = useState(0);

    // ── isCryptoUser: true if this user has ever authed a wallet ───────────
    // Checks three sources: active wallet session, linked methods from server,
    // and localStorage verified_ key. This ensures wallet-only UI stays visible
    // even if the wallet SDK briefly disconnects.
    const hasVerifiedKey = typeof window !== 'undefined' && Object.keys(localStorage).some(k => k.startsWith('verified_') && localStorage.getItem(k) === 'true');
    const isCryptoUser = !!(
        (activeAccount && isVerified && authMethod === 'wallet') ||
        linkedMethods?.walletLinked ||
        hasVerifiedKey
    );

    const refreshCredits = useCallback(async () => {
        const address = activeAccount?.address || linkedMethods?.walletAddress;
        if (!address) {
            if (auth.currentUser) {
                try {
                    const snap = await get(ref(db, `socialUsers/${auth.currentUser.uid}/brineCredits`));
                    setBrineCredits(snap.exists() ? snap.val() : 0);
                } catch { setBrineCredits(0); }
            }
            return;
        }
        try {
            const snap = await get(ref(db, `users/${address.toUpperCase()}/brineCredits`));
            setBrineCredits(snap.exists() ? snap.val() : 0);
        } catch { setBrineCredits(0); }
    }, [activeAccount?.address, linkedMethods?.walletAddress]);

    // ── Profile Fetching ─────────────────────────────────────────────────────
    const refreshProfile = useCallback(async () => {
        const address = activeAccount?.address || linkedMethods?.walletAddress;
        if (!address && !socialUser) { setUserProfile(null); return; }

        try {
            if (address) {
                const profileData = await firebaseUtils.initUserProfile(address);
                setUserProfile(profileData);
                if ((profileData as any)?.brineCredits !== undefined) {
                    setBrineCredits((profileData as any).brineCredits);
                } else {
                    await refreshCredits();
                }
            } else if (socialUser) {
                const getSocial = httpsCallable(functions, 'getSocialProfile');
                const result = await getSocial();
                const data = result.data as any;
                if (data.success) {
                    setUserProfile(data.profile);
                    if (data.profile?.brineCredits !== undefined) {
                      setBrineCredits(data.profile.brineCredits);
                    }
                } else {
                    setUserProfile({
                        userId: socialUser.uid,
                        displayName: socialUser.displayName || socialUser.email?.split('@')[0] || 'Social User',
                        profileImageUrl: socialUser.photoURL || '🥒',
                        stats: { level: 1, totalPoints: 0 }
                    } as any);
                }
            }
        } catch (err) {
            console.warn('[AuthContext] refreshProfile failed:', err);
        }
    }, [activeAccount?.address, socialUser?.uid, linkedMethods?.walletAddress, refreshCredits]);

    useEffect(() => {
        refreshProfile();
    }, [activeAccount?.address, socialUser?.uid, linkedMethods?.walletAddress, refreshProfile]);

    // ── Auth State Listener ──────────────────────────────────────────────────
    useEffect(() => {
        // Immediate guest load from localStorage
        const savedGuest = localStorage.getItem('pickle_pong_guest');
        if (savedGuest && !activeAccount && !socialUser) {
            try {
                const guest = JSON.parse(savedGuest);
                setGuestUser(guest);
                setAuthMethod('guest');
            } catch (e) {
                localStorage.removeItem('pickle_pong_guest');
            }
        }

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user && !user.isAnonymous) {
                setSocialUser(user);
                const provider = user.providerData[0]?.providerId;

                // Determine auth method — wallet custom tokens have no provider
                // so we must check for an active wallet connection or localStorage key
                // before falling through to 'passkey'.
                if (provider === 'google.com') {
                    if (authMethod !== 'wallet') setAuthMethod('google');
                } else if (provider === 'password') {
                    if (authMethod !== 'wallet') setAuthMethod('email');
                } else if (user.displayName?.toLowerCase().includes('discord') || user.photoURL?.includes('discord')) {
                    if (authMethod !== 'wallet') setAuthMethod('discord');
                } else if (!provider) {
                    // No provider = custom token (wallet) or passkey.
                    const isWalletPersisted = activeAccount ? localStorage.getItem(`verified_${activeAccount.address.toUpperCase()}`) === 'true' : false;
                    if (isWalletPersisted) {
                        setAuthMethod('wallet');
                    } else if (authMethod !== 'wallet') {
                        setAuthMethod('passkey');
                    }
                }

                await user.getIdToken(true);
                await refreshLinkedMethods();
                setIsVerified(true);
            } else {
                setSocialUser(null);
                setLinkedMethods(null);
                setIsVerified(false);
                if (authMethod !== 'guest') setAuthMethod(null);
            }
        });
        return () => unsubscribe();
    }, [authMethod, activeAccount]);

    // ── Wallet Sync ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (activeAccount || socialUser) {
            // Clear guest session if we have a real auth method
            if (guestUser) {
                setGuestUser(null);
                localStorage.removeItem('pickle_pong_guest');
            }
            
            if (activeAccount) {
                const isPersisted = localStorage.getItem(`verified_${activeAccount.address.toUpperCase()}`) === 'true';
                setIsVerified(isPersisted || !!socialUser);
                // Ensure authMethod is set to 'wallet' as soon as an account is connected, 
                // even if not yet verified. This prevents the auto-guest timer from firing.
                setAuthMethod('wallet');
            }
        } else {
            setIsVerified(false);
            setAuthMethod(null);
        }
    }, [activeAccount, socialUser, guestUser]);

    // ── Pre-warm Auth Challenge ──────────────────────────────────────────
    useEffect(() => {
        if (activeAccount && !isVerified) {
            // Silently warm up the cloud function to reduce cold-start latency during verify click
            httpsCallable(functions, 'getAuthChallengeV2')().catch(() => {});
        }
    }, [activeAccount?.address, isVerified]);

    // ── Auto-Guest Initialization ──────────────────────────────────────────
    useEffect(() => {
        // If after 1.5 seconds we still have no auth method, no active account, 
        // and no social user, then silently initialize a guest.
        const timer = setTimeout(() => {
            if (!authMethod && !activeAccount && !socialUser && !guestUser) {
                console.log('[Auth] Silent Guest Auto-Initialization');
                loginAsGuest();
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, [authMethod, activeAccount, socialUser, guestUser]);

    // ── Linked Methods ───────────────────────────────────────────────────────
    const refreshLinkedMethods = async () => {
        if (!auth.currentUser) return;
        try {
            const getLinked = httpsCallable(functions, 'getLinkedMethods');
            const result = await getLinked();
            if (!auth.currentUser) return;

            const data = result.data as any;
            setLinkedMethods({
                ...data.links,
                providers: data.providers || [],
                walletLinked: !!data.links?.walletAddress
            });
            
            if (activeAccount && data.links?.walletAddress === activeAccount.address.toUpperCase()) {
                setIsVerified(true);
                localStorage.setItem(`verified_${activeAccount.address.toUpperCase()}`, 'true');
            }
        } catch (err) {
            console.warn('[Auth] refreshLinkedMethods failed:', err);
        }
    };

    // ── Actions ──────────────────────────────────────────────────────────────
    const verifyAccount = async () => {
        if (!activeAccount || !signTransactions || isVerifying) return;
        setIsVerifying(true);
        try {
            // Speed is critical for mobile wallet deep-linking. 
            // Avoid withRetry on the first attempt to keep the user gesture 'fresh'.
            const [params, challengeResponse] = await Promise.all([
                authAlgodClient.getTransactionParams().do(),
                httpsCallable(functions, 'getAuthChallengeV2')()
            ]);
            const { nonce, timestamp } = challengeResponse.data as any;
            const noteStr = `Pickle Pong Auth:${nonce}:${timestamp}`;
            const note = new TextEncoder().encode(noteStr);
            const txn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
                sender: activeAccount.address,
                receiver: 'I5VOLJMONSXTEFKRSY6XEL6ADSPL7MZCIZFGKC3WGDHEXOIO6BRMQHDWTA',
                amount: 0,
                note,
                suggestedParams: { ...params, fee: 1000, flatFee: true },
            });
            const encodedTxn = algosdk.encodeUnsignedTransaction(txn);
            const signedResult = await signTransactions([encodedTxn]);
            if (!signedResult || !signedResult[0]) throw new Error('Cancelled');
            const verifyAuth = httpsCallable(functions, 'verifyWalletAuthV2');
            const verificationResult = await verifyAuth({
                sigB64: algosdk.bytesToBase64(signedResult[0]),
                txnB64: algosdk.bytesToBase64(encodedTxn),
                walletAddress: activeAccount.address,
                nonce,
                timestamp
            });
            const { customToken } = verificationResult.data as any;
            if (customToken) {
                const firebaseUser = await signInWithCustomToken(auth, customToken);
                const upperAddress = activeAccount.address.toUpperCase();
                await set(ref(db, `uid_mapping/${upperAddress}`), firebaseUser.user.uid);
                setIsVerified(true);
                setAuthMethod('wallet');
                localStorage.setItem(`verified_${upperAddress}`, 'true');
                if (socialUser) {
                    const linkFn = httpsCallable(functions, 'linkAuthMethod');
                    await linkFn({ walletAddress: activeAccount.address });
                    await refreshLinkedMethods();
                }
            }
        } catch (err: any) {
            console.error('[Auth] Verification failed:', err);
        } finally {
            setIsVerifying(false);
        }
    };

    const signInWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            setSocialUser(result.user);
            setAuthMethod('google');
            const createProfile = httpsCallable(functions, 'createSocialProfile');
            await createProfile();
            await refreshLinkedMethods();
            setIsVerified(true);
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') console.error(err);
        }
    };

    const signInWithEmail = async (email: string, pass: string) => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, pass);
            setSocialUser(result.user);
            setAuthMethod('email');
            await refreshLinkedMethods();
        } catch (err: any) {
            console.error('[Auth] Sign in failed:', err);
            throw err;
        }
    };

    const signUpWithEmail = async (email: string, pass: string) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, pass);
            setSocialUser(result.user);
            setAuthMethod('email');
            const createProfile = httpsCallable(functions, 'createSocialProfile');
            await createProfile();
            await refreshLinkedMethods();
        } catch (err: any) {
            console.error('[Auth] Sign up failed:', err);
            throw err;
        }
    };

    const signInWithPasskey = async () => {
        try {
            const getChallenge = httpsCallable(functions, 'getPasskeyChallenge');
            const { challenge, rpId } = (await getChallenge()).data as any;
            const assertion = await navigator.credentials.get({
                publicKey: { challenge: base64urlToBuffer(challenge), rpId, userVerification: "required" }
            }) as any;
            const verifyAssertion = httpsCallable(functions, 'verifyPasskeyAssertion');
            const { customToken } = (await verifyAssertion({
                challenge,
                assertion: {
                    id: assertion.id,
                    rawId: bufferToBase64url(assertion.rawId),
                    response: {
                        authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
                        clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
                        signature: bufferToBase64url(assertion.response.signature),
                        userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null
                    }
                }
            })).data as any;
            await signInWithCustomToken(auth, customToken);
            setAuthMethod('passkey');
            setIsVerified(true);
        } catch (err: any) {
            if (err.name !== 'NotAllowedError') console.error(err);
        }
    };

    const signInWithDiscord = () => {
        const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
        const redirectUri = encodeURIComponent(window.location.origin + "/auth/discord/callback");
        window.location.href = `https://discord.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=identify&state=login`;
    };

    const unlinkWallet = async () => {
        if (!auth.currentUser || !linkedMethods?.walletAddress) return;
        try {
            const unlinkFn = httpsCallable(functions, 'unlinkAuthMethod');
            await unlinkFn({ providerId: 'wallet' });
            const upperAddr = linkedMethods.walletAddress.toUpperCase();
            localStorage.removeItem(`verified_${upperAddr}`);
            setIsVerified(false);
            if (authMethod === 'wallet') setAuthMethod(null);
            await refreshLinkedMethods();
        } catch (err) {
            console.error('[Auth] Unlink wallet failed:', err);
        }
    };

    const unlinkDiscord = async () => {
        if (!auth.currentUser) return;
        try {
            const unlinkFn = httpsCallable(functions, 'unlinkAuthMethod');
            await unlinkFn({ providerId: 'discord' });
            await refreshLinkedMethods();
        } catch (err) { console.error('[Auth] Unlink Discord failed:', err); }
    };

    const unlinkGoogle = async () => {
        if (!auth.currentUser) return;
        try {
            const unlinkFn = httpsCallable(functions, 'unlinkAuthMethod');
            await unlinkFn({ providerId: 'google' });
            await refreshLinkedMethods();
        } catch (err) { console.error('[Auth] Unlink Google failed:', err); }
    };

    const registerPasskey = async () => {
        if (!auth.currentUser) return;
        try {
            const getOptions = httpsCallable(functions, 'getPasskeyRegistrationOptions');
            const options = (await getOptions()).data as any;
            if (options.error) throw new Error(options.message || options.error);
            const credential = await navigator.credentials.create({
                publicKey: {
                    ...options,
                    challenge: base64urlToBuffer(options.challenge),
                    user: { ...options.user, id: base64urlToBuffer(options.user.id) },
                    pubKeyCredParams: [{ alg: -7, type: "public-key" }, { alg: -257, type: "public-key" }],
                    authenticatorSelection: { userVerification: "required", residentKey: "required" }
                }
            }) as any;
            const storeCred = httpsCallable(functions, 'storePasskeyCredential');
            await storeCred({
                challenge: options.challenge,
                credential: {
                    id: credential.id,
                    rawId: bufferToBase64url(credential.rawId),
                    publicKey: bufferToBase64url(credential.response.getPublicKey()),
                    transports: credential.getTransports?.() || []
                }
            });
            await refreshLinkedMethods();
        } catch (err) { console.error(err); }
    };

    const signOut = async () => {
        try {
            Object.keys(localStorage).forEach(key => { if (key.startsWith('verified_')) localStorage.removeItem(key); });
            await firebaseSignOut(auth);
            setSocialUser(null);
            setGuestUser(null);
            setAuthMethod(null);
            setLinkedMethods(null);
            setIsVerified(false);
            setBrineCredits(0);
            window.location.reload();
        } catch (err) { console.error('[Auth] Sign out failed:', err); }
    };

    const loginAsGuest = () => {
        const randomName = GUEST_NAMES[Math.floor(Math.random() * GUEST_NAMES.length)];
        const guest: GuestUser = { isGuest: true, address: 'guest_' + Math.random().toString(36).substr(2, 9), displayName: randomName };
        setGuestUser(guest);
        setAuthMethod('guest');
        localStorage.setItem('pickle_pong_guest', JSON.stringify(guest));
    };

    const logoutGuest = () => {
        setGuestUser(null);
        setAuthMethod(null);
        localStorage.removeItem('pickle_pong_guest');
    };

    const isGuestOrConnected = !!(activeAccount || guestUser || socialUser);

    return (
        <AuthContext.Provider value={{
            activeAccount, signTransactions, socialUser, authMethod, guestUser,
            isGuestOrConnected, isVerified, isVerifying, linkedMethods,
            isCryptoUser, brineCredits, refreshCredits,
            verifyAccount, signInWithGoogle, signInWithPasskey, signInWithEmail, signUpWithEmail,
            registerPasskey, signInWithDiscord, unlinkWallet, unlinkDiscord, unlinkGoogle,
            signOut, refreshLinkedMethods, loginAsGuest, logoutGuest,
            showAuthModal, setShowAuthModal,
            preferredAuthView, setPreferredAuthView,
            userProfile, refreshProfile
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
}
